import { defineComponent as _defineComponent } from 'vue'

import { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'ListArticle',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import { storeToRefs } from 'pinia';
import { CollectionType } from '@/enums/collection-type';

import { getArticleBackdrop, getLocalizedArticleImage } from '@/features/editorial/helper';
import { ListProvision } from '@/features/editorial/composables/useArticle';
import { EditorialListTitleFragment } from '@/features/editorial/graphql/fragments/EditorialListTitle.fragment';

import ListArticleItem from '@/features/editorial/components/ListArticleItem.vue';
import ListOffersComparison from '@/features/editorial/components/ListOffersComparison.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import GuideList from '@/features/editorial/components/GuideList.vue';
import { useGlobalQueryLoading } from '@/helpers/composables/useApollo';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useEditorialTracking } from '../tracking';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';

import { computed, ref, watch } from 'vue';

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    article: null,
    mainObject: null
  },
  emits: ["load-next"],
  setup(__props: any, { expose }: { emit: ({
	(e: 'load-next', payload: { event: any }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	article: ListProvision['article'];
	mainObject: ListProvision['mainObject'];
};

const TimelineEmptyState = () =>
	import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue');
const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');





const { trackEditorial } = useEditorialTracking();

const { language } = storeToRefs(useLanguageStore());

const { editorial: filters } = storeToRefs(useFilterStore());

const readMore = ref(false);

const size = computed(() => props.mainObject.titles.totalCount);

function getRank(idx: number) {
	if (['', 'ascending'].includes(props.article.titleOrderMode)) {
		return idx + 1;
	}

	if (['descending', 'descending_reversed'].includes(props.article.titleOrderMode)) {
		return size.value - idx;
	}

	return 0;
}

const titles = computed<EditorialListTitleFragment[]>(() => {
	const titles = (props.mainObject.titles.edges ?? []).map(({ node, data }) => {
		const title = { ...node };
		const override = data.overrides?.find(override => override.languageOrLocale === language.value);
		if (override) {
			if (override.title) {
				title.content.title = override.title;
			}
			if (override.shortDescription) {
				title.content.shortDescription = override.shortDescription;
			}
		}
		return title;
	});
	return props.article.titleOrderMode === 'descending_reversed' ? titles.reverse() : titles;
});

// we need to send the title informations to metaStore, so it can determine the usage of `noindex, follow` or not.
const metaStore = useMetaStore();
watch(
	titles,
	items => {
		metaStore.setVisiblePosters(items.length);
	},
	{ immediate: true }
);

const isEmpty = computed(() => titles.value.length === 0 || size.value === 0);

const isAtEndOfList = computed(() => !props.mainObject.titles.pageInfo.hasNextPage);

const isLoading = useGlobalQueryLoading();

const head = computed(() => {
	let socialMediaImageUrl = getLocalizedArticleImage(props.article.images || [], 'social_media', language.value);
	if (!socialMediaImageUrl) {
		socialMediaImageUrl = getArticleBackdrop(props.mainObject, 'backdrop');
	}
	return {
		meta: [
			{ vmid: 'og:image', property: 'og:image', content: socialMediaImageUrl },
			{ vmid: 'og:image:width', property: 'og:image:width', content: '1920' },
			{ vmid: 'og:image:height', property: 'og:image:height', content: '900' },
		],
	};
});

expose({ head });

/* TRACKING */
function trackClick(e: Event) {
	const target = e.target as HTMLElement;
	const anchorElement = target.parentNode?.parentNode;
	if (anchorElement?.nodeName !== 'A') return;

	const property = (anchorElement as HTMLAnchorElement).href;

	trackEditorial('clickArticleLink', {
		spec: { label: property?.includes('justwatch.com') ? 'internal' : 'external', property },
	});
}

return { __sfc: true,TimelineEmptyState, FilterBarSeo, props, trackEditorial, language, filters, readMore, size, getRank, titles, metaStore, isEmpty, isAtEndOfList, isLoading, head, trackClick, CollectionType, ListArticleItem, ListOffersComparison, InfiniteScroll, GuideList, faChevronUp, faChevronDown }
}

})